import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { formatDateOnly } from '../utils/format';

/**
 * MayLogicScratchpad
 *
 * The multi-lien strategy view. Replaces the manual TX Timeline spreadsheet
 * workflow: shows every medication for a project alongside its MayLogic
 * recommendation, lets the attorney edit each "Your Ask" amount, and updates
 * the running totals + estimated client net in real time.
 *
 * Props:
 *   medications   array — the project's medication rows (from displayProject)
 *   project       object — the displayProject (used for case context)
 */
function MayLogicScratchpad({ medications, project, onStartNegotiation, onSaveCaseDefaults }) {
  // Case-level inputs (settlement, fees, costs) — bound to the same
  // case_negotiation_defaults as the CaseFinancials strip at top of the
  // project detail. Edits in either place save to the same record.
  const caseDefaults = project?.case_negotiation_defaults || {};
  const [settlementAmount, setSettlementAmount] = useState(
    caseDefaults.settlement_amount != null ? String(caseDefaults.settlement_amount) : ''
  );
  const [attorneyFeesPct, setAttorneyFeesPct] = useState(
    caseDefaults.attorney_fees_pct != null ? String(caseDefaults.attorney_fees_pct) : '33'
  );
  const [legalCosts, setLegalCosts] = useState(
    caseDefaults.legal_costs != null ? String(caseDefaults.legal_costs) : ''
  );

  // Re-sync if the project's case defaults update from outside (e.g., the
  // attorney edited the Case Financials strip at the top of the page).
  useEffect(() => {
    if (caseDefaults.settlement_amount != null) setSettlementAmount(String(caseDefaults.settlement_amount));
    if (caseDefaults.attorney_fees_pct != null) setAttorneyFeesPct(String(caseDefaults.attorney_fees_pct));
    if (caseDefaults.legal_costs != null) setLegalCosts(String(caseDefaults.legal_costs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseDefaults.settlement_amount, caseDefaults.attorney_fees_pct, caseDefaults.legal_costs]);

  // Persist changes on blur to the case_negotiation_defaults overlay
  const saveCaseField = (field, value) => {
    if (!onSaveCaseDefaults) return;
    const parsed = value === '' ? null : parseFloat(value);
    onSaveCaseDefaults({ [field]: parsed });
  };

  // Per-medication "Your Ask" amounts, keyed by med id.
  const [asks, setAsks] = useState({});

  // Active strategy pill — drives which percentile gets applied when the user
  // clicks Conservative/MayLogic/Aggressive. 'custom' just means the user is
  // editing manually; we don't auto-fill in that mode.
  const [strategy, setStrategy] = useState('maylogic');

  // MayLogic stats keyed by provider_name (cached)
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);

  // Filter to medications with positive billed amounts (excludes zero/missing)
  const billableMeds = useMemo(() => {
    if (!Array.isArray(medications)) return [];
    return medications.filter((m) => {
      const billed = parseFloat(m.original_bill ?? m.lien_amount ?? 0);
      return billed > 0;
    });
  }, [medications]);

  // Initialize asks from each med's existing lien_amount (so the default
  // matches what the team would have asked without MayLogic).
  useEffect(() => {
    const initial = {};
    billableMeds.forEach((m) => {
      if (asks[m.id] === undefined) {
        initial[m.id] = m.lien_amount != null
          ? Number(m.lien_amount).toFixed(2)
          : (m.original_bill != null ? Number(m.original_bill).toFixed(2) : '');
      }
    });
    if (Object.keys(initial).length > 0) {
      setAsks((prev) => ({ ...initial, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billableMeds.length]);

  // Fetch MayLogic stats for each unique provider in parallel
  useEffect(() => {
    const uniqueProviders = [...new Set(billableMeds.map((m) => m.provider_name).filter(Boolean))];
    if (uniqueProviders.length === 0) return;
    const toFetch = uniqueProviders.filter((p) => stats[p] === undefined);
    if (toFetch.length === 0) return;

    setStatsLoading(true);
    Promise.all(
      toFetch.map((name) =>
        axios
          .get(
            `${API_URL}/api/maylogic/provider-stats?name=${encodeURIComponent(name)}`,
            { withCredentials: true }
          )
          .then((res) => [name, res.data])
          .catch(() => [name, null])
      )
    )
      .then((results) => {
        setStats((prev) => {
          const next = { ...prev };
          results.forEach(([name, data]) => {
            next[name] = data;
          });
          return next;
        });
      })
      .finally(() => setStatsLoading(false));
  }, [billableMeds, stats]);

  // ---- Math ----
  const totalBilled = billableMeds.reduce(
    (sum, m) => sum + (parseFloat(m.original_bill ?? m.lien_amount ?? 0) || 0),
    0,
  );
  const totalAsks = billableMeds.reduce(
    (sum, m) => sum + (parseFloat(asks[m.id]) || 0),
    0,
  );

  const settlementNum = parseFloat(settlementAmount) || 0;
  const feesPctNum = parseFloat(attorneyFeesPct) || 0;
  const costsNum = parseFloat(legalCosts) || 0;
  const attorneyFeesAmt = settlementNum * (feesPctNum / 100);
  const preLienNet = settlementNum - attorneyFeesAmt - costsNum;
  const clientNet = preLienNet - totalAsks;
  const lienSavings = totalBilled - totalAsks;

  // ---- Helpers ----
  const formatCurrency = (n) => {
    if (n == null || isNaN(n)) return '—';
    return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const updateAsk = (medId, value) => {
    setAsks((prev) => ({ ...prev, [medId]: value }));
  };

  // Apply a strategy across all rows in one click. Pulls the right percentile
  // from the cached MayLogic stats for each provider:
  //   conservative -> p25 reduction (ask is the floor of what providers accept)
  //   maylogic     -> p50 (the recommender's default — balanced)
  //   aggressive   -> p75 (stretch ask — near the top of what's been accepted)
  // For rows where we have no MayLogic data, the existing value is kept.
  const applyStrategy = (mode) => {
    setStrategy(mode);
    if (mode === 'custom') return; // custom = user edits manually; no auto-fill
    const next = {};
    billableMeds.forEach((m) => {
      const billed = parseFloat(m.original_bill ?? m.lien_amount ?? 0);
      const s = stats[m.provider_name];
      const pct = s?.reduction_pct;
      let reduction = null;
      if (mode === 'conservative') reduction = pct?.p25;
      else if (mode === 'aggressive') reduction = pct?.p75;
      else reduction = pct?.p50;

      if (reduction != null && billed > 0) {
        next[m.id] = Math.max(0, billed * (1 - reduction)).toFixed(2);
      } else {
        next[m.id] = asks[m.id] || (billed ? billed.toFixed(2) : '');
      }
    });
    setAsks(next);
  };

  const resetToLienAmounts = () => {
    const next = {};
    billableMeds.forEach((m) => {
      next[m.id] = m.lien_amount != null
        ? Number(m.lien_amount).toFixed(2)
        : (m.original_bill != null ? Number(m.original_bill).toFixed(2) : '');
    });
    setAsks(next);
  };

  // ---- Per-row acceptance assessment ----
  const acceptanceForRow = (med) => {
    const billed = parseFloat(med.original_bill ?? med.lien_amount ?? 0) || 0;
    const ask = parseFloat(asks[med.id]) || 0;
    const s = stats[med.provider_name];
    if (!s || !s.reduction_pct || billed === 0) {
      return { label: 'No data', klass: styles.acceptanceNone, vsLabel: '' };
    }
    const reduction = (billed - ask) / billed;
    const { p25, p50, p75, max } = s.reduction_pct;
    if (p50 == null) return { label: 'No data', klass: styles.acceptanceNone, vsLabel: '' };

    if (reduction <= p50) {
      return { label: 'Very likely', klass: styles.acceptanceHigh, vsLabel: reduction < (p25 || 0) ? 'Conservative' : 'Matches median' };
    } else if (reduction <= (p75 || 1)) {
      return { label: 'Likely', klass: styles.acceptanceMed, vsLabel: 'More aggressive than median' };
    } else if (reduction <= (max || 1)) {
      return { label: 'Stretch', klass: styles.acceptanceLow, vsLabel: 'Very aggressive' };
    } else {
      return { label: 'Unlikely', klass: styles.acceptanceLow, vsLabel: 'Beyond historical max' };
    }
  };

  if (billableMeds.length === 0) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyEyebrow}>MAYLOGIC STRATEGY</div>
        <p>No medications with positive billed amounts on this project. Add meds with bill amounts on the Meds tab to use the scratchpad.</p>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.eyebrow}>MAYLOGIC STRATEGY</div>
          <h2 style={styles.title}>{project?.client_name || 'Case Strategy'}</h2>
          <div style={styles.subtitle}>
            Edit each "Your Ask" to model different negotiation strategies. Totals update live.
          </div>
        </div>
        <div style={styles.headerActions}>
          <button type="button" style={styles.btnSecondary} onClick={resetToLienAmounts}>
            Reset to Lien Amounts
          </button>
        </div>
      </div>

      {/* Strategy toggle — one-click apply for each percentile */}
      <div style={styles.scenarioBar}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={styles.scenarioLabel}>Strategy</span>
          <div style={styles.scenarioPills}>
            {[
              { id: 'conservative', icon: '🛡️', label: 'Conservative' },
              { id: 'maylogic',     icon: '🎯', label: 'MayLogic' },
              { id: 'aggressive',   icon: '⚡', label: 'Aggressive' },
              { id: 'custom',       icon: '✏️', label: 'Custom' },
            ].map(({ id, icon, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => applyStrategy(id)}
                disabled={statsLoading && id !== 'custom'}
                style={{
                  ...styles.scenarioPill,
                  ...(strategy === id ? styles.scenarioPillActive : {}),
                }}
              >
                <span style={{ fontSize: 13 }}>{icon}</span> {label}
              </button>
            ))}
          </div>
        </div>
        <div style={styles.scenarioHelper}>
          {{
            conservative: '25th-percentile reduction · highest acceptance chance, smaller savings',
            maylogic:     'Median historical reduction · balanced acceptance/savings',
            aggressive:   '75th-percentile reduction · stretch ask, biggest savings if accepted',
            custom:       'You are editing each ask manually',
          }[strategy]}
        </div>
      </div>

      {/* Case context inputs */}
      <div style={styles.caseInputs}>
        <div style={styles.caseInputCol}>
          <label style={styles.caseInputLabel}>Settlement</label>
          <div style={styles.caseInputWrap}>
            <span style={styles.caseInputPrefix}>$</span>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={settlementAmount}
              onChange={(e) => setSettlementAmount(e.target.value)}
              onBlur={(e) => saveCaseField('settlement_amount', e.target.value)}
              style={styles.caseInput}
            />
          </div>
        </div>
        <div style={styles.caseInputCol}>
          <label style={styles.caseInputLabel}>Att. Fees</label>
          <div style={styles.caseInputWrap}>
            <input
              type="number"
              step="0.1"
              placeholder="33"
              value={attorneyFeesPct}
              onChange={(e) => setAttorneyFeesPct(e.target.value)}
              onBlur={(e) => saveCaseField('attorney_fees_pct', e.target.value)}
              style={styles.caseInput}
            />
            <span style={styles.caseInputSuffix}>%</span>
          </div>
        </div>
        <div style={styles.caseInputCol}>
          <label style={styles.caseInputLabel}>Costs</label>
          <div style={styles.caseInputWrap}>
            <span style={styles.caseInputPrefix}>$</span>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={legalCosts}
              onChange={(e) => setLegalCosts(e.target.value)}
              onBlur={(e) => saveCaseField('legal_costs', e.target.value)}
              style={styles.caseInput}
            />
          </div>
        </div>
        <div style={styles.caseStatCol}>
          <div style={styles.caseStatLabel}>Pre-Lien Net</div>
          <div style={styles.caseStatValue}>{settlementNum > 0 ? formatCurrency(preLienNet) : '—'}</div>
        </div>
      </div>

      {/* Lien table */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Provider</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Billed</th>
              <th style={styles.th}>MayLogic Says</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Your Ask</th>
              <th style={styles.th}>Acceptance</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Send</th>
            </tr>
          </thead>
          <tbody>
            {billableMeds.map((m) => {
              const billed = parseFloat(m.original_bill ?? m.lien_amount ?? 0) || 0;
              const s = stats[m.provider_name];
              const median = s?.reduction_pct?.p50;
              const sample = s?.n;
              const confidence = s?.confidence;
              const suggested = (median != null && billed > 0) ? billed * (1 - median) : null;
              const ask = parseFloat(asks[m.id]) || 0;
              const askPct = billed > 0 ? ((billed - ask) / billed) * 100 : 0;
              const accept = acceptanceForRow(m);

              return (
                <tr key={m.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.providerName}>{m.provider_name || 'Unknown'}</div>
                    {m.date && <div style={styles.providerMeta}>{formatDateOnly(m.date)}</div>}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{formatCurrency(billed)}</td>
                  <td style={styles.td}>
                    {sample == null && <span style={styles.mlNone}>Loading…</span>}
                    {sample === 0 && <span style={styles.mlNone}>No history</span>}
                    {sample > 0 && confidence === 'insufficient' && (
                      <span style={styles.mlNone}>Only {sample} case{sample === 1 ? '' : 's'}</span>
                    )}
                    {sample > 0 && confidence !== 'insufficient' && median != null && (
                      <div style={styles.mlBox}>
                        <div style={styles.mlPct}>{Math.round(median * 100)}% off</div>
                        <div style={styles.mlAmount}>≈ {formatCurrency(suggested)}</div>
                        <div style={styles.mlSample}>
                          {sample} cases · {confidence}
                        </div>
                      </div>
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <div style={styles.askInputWrap}>
                      <span style={styles.askInputPrefix}>$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={asks[m.id] ?? ''}
                        onChange={(e) => updateAsk(m.id, e.target.value)}
                        style={styles.askInput}
                      />
                    </div>
                    {billed > 0 && (
                      <div style={styles.askPct}>{askPct.toFixed(0)}% off</div>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={{ ...styles.acceptancePill, ...accept.klass }}>
                      <span style={styles.acceptanceDot}></span>
                      <span>{accept.label}</span>
                    </div>
                    {accept.vsLabel && <div style={styles.vsLabel}>{accept.vsLabel}</div>}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() =>
                        onStartNegotiation && onStartNegotiation(m.id, asks[m.id])
                      }
                      disabled={!asks[m.id] || parseFloat(asks[m.id]) <= 0}
                      style={{
                        ...styles.sendBtn,
                        ...((!asks[m.id] || parseFloat(asks[m.id]) <= 0) ? styles.sendBtnDisabled : {}),
                      }}
                      title={!asks[m.id] || parseFloat(asks[m.id]) <= 0
                        ? 'Enter an ask amount first'
                        : `Send negotiation to ${m.provider_name} for $${asks[m.id]}`}
                    >
                      Send →
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div style={styles.totalsGrid}>
        <div style={styles.totalsCol}>
          <div style={styles.totalsLine}>
            <span style={styles.totalsLabel}>Total billed</span>
            <span style={styles.totalsValue}>{formatCurrency(totalBilled)}</span>
          </div>
          <div style={styles.totalsLine}>
            <span style={styles.totalsLabel}>Total of your asks</span>
            <span style={styles.totalsValue}>−{formatCurrency(totalAsks)}</span>
          </div>
          <div style={styles.totalsLine}>
            <span style={styles.totalsLabel}>Lien savings if all accept</span>
            <span style={{ ...styles.totalsValue, color: '#16a34a' }}>{formatCurrency(lienSavings)}</span>
          </div>
        </div>
        <div style={styles.totalsCol}>
          <div style={styles.totalsLine}>
            <span style={styles.totalsLabel}>Pre-lien net</span>
            <span style={styles.totalsValue}>{settlementNum > 0 ? formatCurrency(preLienNet) : '—'}</span>
          </div>
          <div style={styles.totalsLine}>
            <span style={styles.totalsLabel}>Total asks (lien total)</span>
            <span style={styles.totalsValue}>−{formatCurrency(totalAsks)}</span>
          </div>
          <div style={{ ...styles.totalsLine, ...styles.totalsFinal }}>
            <span style={styles.totalsFinalLabel}>Net to Client</span>
            <span style={styles.totalsFinalValue}>
              {settlementNum > 0 ? formatCurrency(clientNet) : 'Enter settlement above'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Styles (inline for self-containment, matching brand) ---
const styles = {
  wrap: { padding: '1rem 0' },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '1.25rem',
    gap: '1rem',
  },
  eyebrow: {
    fontFamily: "'Arial Black', Impact, 'Helvetica Neue', sans-serif",
    fontSize: 11,
    letterSpacing: 2,
    color: '#A1B808',
    marginBottom: 4,
  },
  title: { margin: '0 0 4px', fontSize: 22, fontWeight: 600, color: '#171D29' },
  subtitle: { fontSize: 12, color: '#6b7585' },
  headerActions: { display: 'flex', gap: 8, flexShrink: 0 },
  btnPrimary: {
    background: '#A1B808',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: 6,
    fontFamily: "'Arial Black', Impact, sans-serif",
    fontWeight: 900,
    fontSize: 11,
    letterSpacing: 1,
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  btnSecondary: {
    background: 'white',
    color: '#171D29',
    border: '1px solid #d1d5db',
    padding: '8px 14px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },

  // Strategy pills row
  scenarioBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    marginBottom: '1.25rem',
    gap: 12,
    flexWrap: 'wrap',
  },
  scenarioLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: '#6b7585',
    fontWeight: 600,
    marginRight: 12,
  },
  scenarioPills: {
    display: 'inline-flex',
    background: '#f4f5f7',
    borderRadius: 8,
    padding: 3,
    gap: 2,
  },
  scenarioPill: {
    background: 'transparent',
    border: 0,
    padding: '7px 14px',
    fontSize: 12,
    color: '#6b7585',
    cursor: 'pointer',
    borderRadius: 6,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    transition: 'background 0.15s, color 0.15s, box-shadow 0.15s',
  },
  scenarioPillActive: {
    background: 'white',
    color: '#171D29',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  scenarioHelper: {
    fontSize: 11,
    color: '#9ca3af',
    fontStyle: 'italic',
  },

  // Case context inputs
  caseInputs: {
    background: '#171D29',
    color: 'white',
    borderRadius: 8,
    padding: '16px 20px',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr) 1.4fr',
    gap: 16,
    marginBottom: '1.25rem',
    alignItems: 'flex-end',
  },
  caseInputCol: { display: 'flex', flexDirection: 'column' },
  caseInputLabel: { fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.6, marginBottom: 4 },
  caseInputWrap: { display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.06)', borderRadius: 4, padding: '6px 10px' },
  caseInputPrefix: { color: 'rgba(255,255,255,0.5)', marginRight: 4, fontSize: 13 },
  caseInputSuffix: { color: 'rgba(255,255,255,0.5)', marginLeft: 4, fontSize: 13 },
  caseInput: { flex: 1, background: 'transparent', border: 0, color: 'white', fontSize: 14, outline: 'none', minWidth: 0 },
  caseStatCol: { background: 'rgba(161,184,8,0.12)', borderRadius: 4, padding: '8px 12px', borderLeft: '3px solid #A1B808' },
  caseStatLabel: { fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#A1B808', marginBottom: 2 },
  caseStatValue: { fontFamily: "'Arial Black', Impact, sans-serif", fontSize: 18 },

  // Table
  tableWrap: { background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '1.25rem' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    background: '#fafafa',
    padding: '10px 14px',
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: '#6b7585',
    fontWeight: 600,
    borderBottom: '1px solid #e5e7eb',
    textAlign: 'left',
  },
  tr: { borderBottom: '1px solid #f1f3f5' },
  td: { padding: '12px 14px', verticalAlign: 'middle', fontSize: 13 },
  providerName: { fontWeight: 600, color: '#171D29' },
  providerMeta: { fontSize: 11, color: '#9ca3af', marginTop: 2 },

  mlNone: { color: '#9ca3af', fontSize: 12, fontStyle: 'italic' },
  mlBox: {
    background: 'rgba(161,184,8,0.08)',
    padding: '6px 10px',
    borderRadius: 4,
    borderLeft: '3px solid #A1B808',
    display: 'inline-block',
  },
  mlPct: { fontFamily: "'Arial Black', Impact, sans-serif", fontSize: 13, color: '#4F5A04' },
  mlAmount: { fontSize: 11, color: '#6b7585', marginTop: 1 },
  mlSample: { fontSize: 10, color: '#9ca3af', marginTop: 1 },

  askInputWrap: { display: 'inline-flex', alignItems: 'center' },
  askInputPrefix: { background: '#fafafa', border: '1px solid #d1d5db', borderRight: 0, borderRadius: '4px 0 0 4px', padding: '6px 8px', color: '#6b7585', fontSize: 12 },
  askInput: { border: '1px solid #d1d5db', borderRadius: '0 4px 4px 0', padding: '6px 8px', width: 96, textAlign: 'right', fontSize: 13, fontWeight: 600 },
  askPct: { fontSize: 11, color: '#6b7585', marginTop: 4 },

  acceptancePill: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  acceptanceDot: { width: 7, height: 7, borderRadius: '50%', background: 'currentColor' },
  acceptanceHigh: { background: 'rgba(22,163,74,0.1)', color: '#16a34a' },
  acceptanceMed: { background: 'rgba(202,138,4,0.1)', color: '#ca8a04' },
  acceptanceLow: { background: 'rgba(220,38,38,0.1)', color: '#dc2626' },
  acceptanceNone: { background: '#f4f5f7', color: '#9ca3af' },
  vsLabel: { fontSize: 10, color: '#9ca3af', marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.5 },

  // Totals
  totalsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, background: '#fafafa', borderRadius: 8, padding: '18px 22px' },
  totalsCol: { display: 'flex', flexDirection: 'column' },
  totalsLine: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13 },
  totalsLabel: { color: '#6b7585' },
  totalsValue: { fontWeight: 600, color: '#171D29' },
  totalsFinal: { borderTop: '2px solid #171D29', marginTop: 8, paddingTop: 10 },
  totalsFinalLabel: { fontFamily: "'Arial Black', Impact, sans-serif", fontSize: 13, color: '#171D29', textTransform: 'uppercase', letterSpacing: 1 },
  totalsFinalValue: { fontFamily: "'Arial Black', Impact, sans-serif", fontSize: 20, color: '#A1B808' },

  emptyState: { padding: '2rem', textAlign: 'center', color: '#6b7585' },
  emptyEyebrow: { fontFamily: "'Arial Black', Impact, sans-serif", fontSize: 11, letterSpacing: 2, color: '#A1B808', marginBottom: 8 },

  // Per-row Send button — fires off the negotiation with the current "Your Ask"
  // amount. Reuses the existing emailModal flow on the parent so emails,
  // legal-costs, attorney-fees-pct etc. all get the same validation/UX as
  // sending from the Meds tab.
  sendBtn: {
    background: '#A1B808',
    color: 'white',
    border: 'none',
    padding: '6px 14px',
    borderRadius: 6,
    fontFamily: "'Arial Black', Impact, sans-serif",
    fontWeight: 900,
    fontSize: 11,
    letterSpacing: 1,
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  sendBtnDisabled: {
    background: '#d1d5db',
    color: '#6b7585',
    cursor: 'not-allowed',
  },
};

export default MayLogicScratchpad;
